import KeycloakJS, {
  KeycloakError,
  KeycloakInstance,
  KeycloakResourceAccess,
  KeycloakRoles,
  KeycloakTokenParsed,
} from 'keycloak-js';
import { difference, pathOr } from 'ramda';
import React, { useState } from 'react';

import { useConfig } from './ConfigurationProvider';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { ExternalRedirect } from '../router/ExternalRedirect';
// eslint-disable-next-line import-x/no-cycle
import { authenticatedStateMock } from '../testUtils/authorizationMock';
import i18n, { DEFAULT_LOCALE } from '../translations/i18n';
import { dateFnsResources, supportedLocales } from '../translations/resources';
import { Values } from '../types';
import { getTimeZone } from '../utils/date';

const DEFAULT_MIN_VALIDITY_OF_TOKEN_BEFORE_EXPIRATION = 3; // Value in seconds

export interface AuthorizationHeadersType {
  Authorization: string;
  ['Tenant-ID']: string;
}
export const createAuthHeaders = (token: string, tenant: string): AuthorizationHeadersType => ({
  Authorization: `Bearer ${token}`,
  'Tenant-ID': tenant,
});

export const keycloakUserHasPermissions =
  (keycloak: Authenticated['keycloak']) =>
  (desiredPermissions: PermissionsType[] = [], requireAllPermissions = true) => {
    const permissionsPerClient: KeycloakResourceAccess = pathOr({}, ['resourceAccess'], keycloak);

    // join permissions from all clients removing duplicates
    const userPermissions = Array.from(
      new Set(
        Object.values(permissionsPerClient).reduce(
          (allPermissions: string[], client: KeycloakRoles) => allPermissions.concat(client.roles),
          []
        )
      )
    );

    if (!desiredPermissions.length && !!userPermissions.length) {
      return true;
    }

    if (!userPermissions.length) {
      return false;
    }

    if (!requireAllPermissions) {
      return difference(desiredPermissions, userPermissions).length !== desiredPermissions.length;
    }

    const containsAllPermissions = !difference(desiredPermissions, userPermissions).length;

    return containsAllPermissions;
  };

/**
 * @link getSessionTimeoutFromRefreshToken
 * @returns {number} - Session Expiration time from Keycloak refresh token as (exp - iat) as number in milliseconds.
 * Return 0 ms if `token` / `exp` / `iat` is not defined.
 */
export const getSessionTimeoutFromRefreshToken = (refreshTokenParsed: KeycloakTokenParsed | undefined): number => {
  if (!refreshTokenParsed) {
    return 0; // something went wrong, logout immediately
  }

  const { exp, iat } = refreshTokenParsed;
  if (!exp || !iat) {
    return 0; // something went wrong, logout immediately
  }

  return (exp - iat) * 1000; // return in millis
};

interface Pending {
  status: 'pending';
}
export interface Authenticated {
  forceUpdateToken: () => Promise<boolean>;
  isTokenInvalid: (minValidity?: number) => boolean;
  keycloak: KeycloakInstance;
  locale: string;
  localeObj?: Locale;
  logout: (options?: { redirectUri: string }) => void;
  manageAccountUrl: string;
  sessionIdleTimeDuration: number; // in millis
  status: 'authenticated';
  timezone: string;
  userHasPermissions: (desiredPermissions?: PermissionsType[], requireAllPermissions?: boolean) => boolean;
  userId: string;
  userName: string;
}

interface NonAuthenticated {
  status: 'non-authenticated';
  redirectUrl: string;
}

export type Authorization = Pending | Authenticated | NonAuthenticated;

const initialState: Pending = {
  status: 'pending',
};

export const AuthorizationContext = React.createContext(authenticatedStateMock);

export const AuthorizationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { appUrl, authUrl, clientId, tenant } = useConfig();
  const [auth, setAuth] = useState<Authorization>(initialState);

  React.useEffect(() => {
    const keycloak: KeycloakInstance = KeycloakJS({
      clientId,
      realm: tenant,
      url: authUrl,
    });

    keycloak.onReady = (authenticated?: boolean) => {
      if (authenticated === false) {
        setAuth({
          status: 'non-authenticated',
          redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
        });
      }
    };

    keycloak.onAuthSuccess = async () => {
      // `tokenParsed` contains user data that are not defined in types
      const {
        tokenParsed: {
          locale = DEFAULT_LOCALE, // fallback to default language
          name: fullUserName,
          preferred_username: userName,
          sub,
          timezone: { zoneId } = {} as any, // Default value as quick-fix to work with older KC version
        } = {} as any,
      } = keycloak;

      const userLocale: string = supportedLocales.includes(locale) ? locale : DEFAULT_LOCALE;

      i18n.changeLanguage(userLocale);

      let localeObj: Locale | undefined;
      const dateFnsPath = dateFnsResources[userLocale];

      if (userLocale !== DEFAULT_LOCALE && dateFnsPath) {
        try {
          localeObj = await import(`date-fns/locale/${dateFnsPath}/index.js`);
        } catch {
          console.warn(`dateFns locale object for "${userLocale}" (on path "${dateFnsPath}") was not found`);
        }
      }

      setAuth({
        keycloak,
        localeObj,
        status: 'authenticated',
        locale: userLocale,
        logout: () => {
          keycloak.logout({ redirectUri: appUrl });
        },
        forceUpdateToken: async () => {
          /**
           * According to implementation details of KC library `-1` is used to force refresh and log appropriate message
           * if logging in KC library is enabled.
           *
           * minValidity is in seconds, and by default its set to 5s, therefore it is not sufficient to use 0s here.
           */
          return keycloak
            .updateToken(-1)
            .then((isAuthenticated) => {
              return isAuthenticated;
            })
            .catch(() => {
              return false;
            });
        },
        isTokenInvalid: (minValidity = DEFAULT_MIN_VALIDITY_OF_TOKEN_BEFORE_EXPIRATION) =>
          keycloak.isTokenExpired(minValidity),
        manageAccountUrl: keycloak.createAccountUrl(),
        userHasPermissions: keycloakUserHasPermissions(keycloak),
        userId: sub,
        // using userName (preferred_username) as a fallback value considering it is a required field in KC
        userName: fullUserName || userName,
        // use browser's timezone in case of user has not defined timezone in his user account
        timezone: zoneId ?? getTimeZone(),
        sessionIdleTimeDuration: getSessionTimeoutFromRefreshToken(keycloak.refreshTokenParsed),
      });
    };

    keycloak.onAuthError = (errorData: KeycloakError) => console.error(`Authorization: ${JSON.stringify(errorData)}`);

    keycloak.onAuthLogout = () => {
      setAuth({
        status: 'non-authenticated',
        redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
      });
    };

    keycloak.onAuthRefreshError = () => {
      setAuth({
        status: 'non-authenticated',
        redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
      });
    };

    keycloak.onAuthRefreshSuccess = () => {
      // update session Idle time duration if it was changed

      if (auth.status === 'authenticated') {
        const sessionTimeout = getSessionTimeoutFromRefreshToken(auth.keycloak.refreshTokenParsed);
        if (sessionTimeout !== auth.sessionIdleTimeDuration) {
          setAuth((prev) => ({
            ...prev,
            sessionIdleTimeDuration: sessionTimeout,
          }));
        }
      }
    };

    keycloak.onTokenExpired = () => {
      console.log('Authorization: Access token expired, updating...');
      keycloak.updateToken(9999);
    };

    keycloak
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${appUrl}/silent-check-sso.html`,
      })
      .then((isAuthenticated) => {
        if (isAuthenticated) {
          console.debug('Keycloak init was successful and user is authenticated.');
          return;
        }
        console.warn('Keycloak init was successful, but user is not authenticated. Redirecting to login...');
        keycloak.login();
      })
      .catch(() => {
        console.warn('Keycloak init failed! Redirecting to login...');
        keycloak.login();
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (auth.status === 'pending') {
    return <LoadingOverlay loading />;
  }

  if (auth.status === 'non-authenticated') {
    return <ExternalRedirect to={auth.redirectUrl} />;
  }

  return <AuthorizationContext.Provider value={auth}>{children}</AuthorizationContext.Provider>;
};

export const AuthorizationConsumer = AuthorizationContext.Consumer;

export const useAuth = () => React.useContext(AuthorizationContext);

export const Permissions = {
  ViewForecast: 'WFM_VIEW_FORECAST',
  ViewSchedule: 'WFM_VIEW_SCHEDULE',
  ViewQueue: 'WFM_VIEW_QUEUE',
  ViewAgent: 'WFM_VIEW_AGENT',
  ViewAgentSchedule: 'WFM_VIEW_AGENTS_SCHEDULE',
  CreateForecast: 'WFM_CREATE_FORECAST',
  CreateSchedule: 'WFM_CREATE_SCHEDULE',
  CreateQueue: 'WFM_CREATE_QUEUE',
  EditForecast: 'WFM_EDIT_FORECAST',
  EditSchedule: 'WFM_EDIT_SCHEDULE',
  EditQueue: 'WFM_EDIT_QUEUE',
  EditAgent: 'WFM_EDIT_AGENT',
  DeleteForecast: 'WFM_DELETE_FORECAST',
  DeleteSchedule: 'WFM_DELETE_SCHEDULE',
  DeleteQueue: 'WFM_DELETE_QUEUE',
  FileUpload: 'WFM_FILE_UPLOAD',
  ExportSchedule: 'WFM_EXPORT_SCHEDULE',
  UserManagementAdmin: 'user-management-admin',
  ViewSkill: 'WFM_VIEW_SKILL',
  EditSkill: 'WFM_EDIT_SKILL',
  DeleteSkill: 'WFM_DELETE_SKILL',
  CreateSkill: 'WFM_CREATE_SKILL',
  ViewAppSwitcher: 'WFM_VIEW_APP_SWITCHER',

  // Auto scheduler
  ViewScheduleJob: 'WFM_VIEW_SCHEDULE_JOB',
  CreateAutoScheduleJob: 'WFM_CREATE_SCHEDULE_JOB',

  // Shift Template
  CreateShiftTemplate: `WFM_CREATE_SHIFT_TEMPLATE`,
  EditShiftTemplate: `WFM_EDIT_SHIFT_TEMPLATE`,
  DeleteShiftTemplate: `WFM_DELETE_SHIFT_TEMPLATE`,
  ViewShiftTemplate: `WFM_VIEW_SHIFT_TEMPLATE`,

  // RTA Notification Settings
  ViewRtaNotificationSettings: 'WFM_VIEW_RTA_NOTIFICATION_SETTINGS',
  EditRtaNotificationSettings: 'WFM_EDIT_RTA_NOTIFICATION_SETTINGS',
  ViewRealTimeAdherenceEvents: 'WFM_VIEW_RTA_EVENTS', // allows user to receive any kind of push or web socketed notification events for RTA

  // Adherence
  ViewAdherenceSettings: 'WFM_VIEW_ADHERENCE_SETTINGS',
  CreateAdherenceSettings: 'WFM_CREATE_ADHERENCE_SETTINGS',
  EditAdherenceSettings: 'WFM_EDIT_ADHERENCE_SETTINGS',
  DeleteAdherenceSettings: 'WFM_DELETE_ADHERENCE_SETTINGS',
  ViewAdherenceHistory: 'WFM_VIEW_ADHERENCE_HISTORY',
  ViewRealTimeAdherence: 'WFM_VIEW_RTA',

  // QM Roles
  LiveMonitorView: 'LIVE_MONITOR_VIEW',
  SpeechrecAdmin: 'SPEECHREC_ADMIN',
  ViewConversationsScreen: 'VIEW_CONVERSATIONS_SCREEN',
  ViewReviews: 'VIEW_REVIEWS',
  ViewSurveys: 'VIEW_SURVEYS',
  ViewTrainings: 'VIEW_TRAININGS',

  // Intraday
  ViewIntraday: 'WFM_VIEW_INTRADAY',
  EditIntradayNotification: 'WFM_EDIT_INTRADAY_NOTIFICATION',
  ViewIntradayNotification: 'WFM_VIEW_INTRADAY_NOTIFICATION',
  ViewIntradayEvents: 'WFM_VIEW_INTRADAY_EVENTS', // allows user to receive any kind of push or web socketed notification events for Intraday

  // Requests (PTOs, Shift swaps, etc.)
  AgentViewRequests: 'WFM_AGENT_VIEW_REQUESTS',
  ViewScheduleRequests: 'WFM_VIEW_SCHEDULE_REQUESTS',
  EditScheduleRequests: 'WFM_EDIT_SCHEDULE_REQUESTS',
  PtoRequestsAccess: 'WFM_PTO_REQUESTS_ACCESS',
  ShiftSwapRequestsAccess: 'WFM_SHIFT_SWAP_REQUESTS_ACCESS',

  // Notifications
  EditNotificationSettings: `WFM_EDIT_NOTIFICATION_SETTINGS`,

  // Reports
  ViewReports: `WFM_ANALYTICS`,

  // Schedule elements
  EditScheduleElements: 'WFM_EDIT_SCHEDULE_ELEMENTS',
  ViewScheduleElements: 'WFM_VIEW_SCHEDULE_ELEMENTS',
} as const;

export type PermissionsType = Values<typeof Permissions>;
